import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Container, Typography, Paper, Grid, Button, CircularProgress,
  Alert, TextField, MenuItem, Select, InputLabel, FormControl, Box, FormLabel, FormGroup, FormControlLabel, Checkbox, RadioGroup, Radio
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Autocomplete from '@mui/material/Autocomplete';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CategoryIcon from '@mui/icons-material/Category';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import config from '../../config';

const ConvertLead = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form State
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [clientOption, setClientOption] = useState('existing'); // 'existing' or 'new'
  const [leadDetails, setLeadDetails] = useState(null);

  const [formData, setFormData] = useState({
    projectName: '',
    country: '',
    division: '',
    industry: '',
    buildingsIndustryType: '',
    areaKsa: '',
    opportunitySource: '',
    currency: '',
    priceList: '',
    exchangeRate: 1.0,
    opportunityStatus: '',
    opportunityClosedStatus: '',
    newClientName: '',
    newClientEmail: '',
    newClientPhone: '',
    newClientContactInfo: '',
    newClientCountryId: ''
  });

  const [files, setFiles] = useState({
    license_1: null,
    license_2: null,
    tax_card: null,
    establishment_card: null,
    application_pdf: null
  });

  const [countries, setCountries] = useState([]);

  const [crmOptions, setCrmOptions] = useState({
    country: [],
    division: [],
    industry: [],
    buildingsIndustryType: [],
    areaKsa: [],
    opportunitySource: [],
    currency: [],
    priceList: [],
    opportunityStatus: [],
    opportunityClosedStatus: []
  });

  const [services, setServices] = useState({
    Testing: false,
    Inspection: false,
    Certification: false,
    Knowledge: false,
    Engineering: false,
    Metadrill: false,
  });

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        const headers = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
        const [leadRes, countryRes, accRes, optRes] = await Promise.all([
          axios.get(`${config.apiUrl || config}/leads/${id}`, headers),
          axios.get(`${config.apiUrl || config}/setup/countries`, headers),
          axios.get(`${config.apiUrl || config}/leads/crm/accounts`, headers),
          axios.get(`${config.apiUrl || config}/leads/crm/options`, headers)
        ]);
        setLeadDetails(leadRes.data);
        setCountries(countryRes.data);
        setAccounts(accRes.data);
        setCrmOptions(optRes.data);
      } catch (err) {
        console.error('Error fetching initial data', err);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (leadDetails) {
      setFormData(prev => {
        let defaultCountryId = prev.newClientCountryId;
        let defaultCrmCountry = prev.country;

        if (leadDetails.address1_country) {
          // Try mapping to newClientCountryId
          if (countries.length > 0 && !defaultCountryId) {
             const matched = countries.find(c => c.name.toLowerCase() === leadDetails.address1_country.toLowerCase());
             if (matched) defaultCountryId = matched.id;
          }
          // Try mapping to formData.country (CRM Options)
          if (crmOptions.country && crmOptions.country.length > 0 && !defaultCrmCountry) {
             const crmMatched = crmOptions.country.find(opt => opt.label.toLowerCase() === leadDetails.address1_country.toLowerCase());
             if (crmMatched) defaultCrmCountry = crmMatched.value;
          }
        }

        return {
          ...prev,
          projectName: prev.projectName || leadDetails.description || leadDetails.subject || leadDetails.companyname || leadDetails.fullname || '',
          newClientCountryId: defaultCountryId,
          country: defaultCrmCountry
        };
      });
    }

    if (leadDetails && accounts.length > 0) {
      // Check if lead company name matches any existing account automatically
      const leadCompanyName = (leadDetails.companyname || leadDetails.fullname || '').trim().toLowerCase();
      const existingMatch = accounts.find(a => a.name.trim().toLowerCase() === leadCompanyName);
      
      if (existingMatch) {
         setClientOption('existing');
         setSelectedAccount(existingMatch);
      } else {
         setClientOption('new');
         setFormData(prev => ({
           ...prev,
           newClientName: leadDetails.companyname || leadDetails.fullname || '',
           newClientEmail: leadDetails.emailaddress1 || '',
           newClientPhone: leadDetails.telephone1 || leadDetails.mobilephone || ''
         }));
      }
    }
  }, [leadDetails, accounts, countries, crmOptions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFiles({ ...files, [e.target.name]: e.target.files[0] });
  };

  const handleServiceChange = (event) => {
    setServices({ ...services, [event.target.name]: event.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const formPayload = new FormData();
      
      // Append normal data logic
      formPayload.append('createNewClient', clientOption === 'new');
      if (clientOption === 'existing' && selectedAccount) {
        formPayload.append('accountId', selectedAccount.accountid);
      }
      
      if (clientOption === 'new') {
        formPayload.append('newAccountName', formData.newClientName);
        formPayload.append('newClientName', formData.newClientName);
        formPayload.append('newClientEmail', formData.newClientEmail);
        formPayload.append('newClientPhone', formData.newClientPhone);
        formPayload.append('newClientContactInfo', formData.newClientContactInfo);
        formPayload.append('newClientCountryId', formData.newClientCountryId);
        
        // Append files
        if (files.license_1) formPayload.append('license_1', files.license_1);
        if (files.license_2) formPayload.append('license_2', files.license_2);
        if (files.tax_card) formPayload.append('tax_card', files.tax_card);
        if (files.establishment_card) formPayload.append('establishment_card', files.establishment_card);
        if (files.application_pdf) formPayload.append('application_pdf', files.application_pdf);
      }

      formPayload.append('projectName', formData.projectName);
      formPayload.append('country', formData.country);
      formPayload.append('division', formData.division);
      
      const selectedCountryLabel = crmOptions.country?.find(c => c.value === formData.country)?.label;
      if (selectedCountryLabel && selectedCountryLabel.toLowerCase() === 'saudi arabia') {
        formPayload.append('areaKsa', formData.areaKsa);
      }

      formPayload.append('industry', formData.industry);
      formPayload.append('buildingsIndustryType', formData.buildingsIndustryType);
      formPayload.append('opportunitySource', formData.opportunitySource);
      formPayload.append('currency', formData.currency);
      formPayload.append('priceList', formData.priceList);
      formPayload.append('exchangeRate', formData.exchangeRate);
      formPayload.append('opportunityStatus', formData.opportunityStatus);
      formPayload.append('opportunityClosedStatus', formData.opportunityClosedStatus);

      const activeServices = Object.keys(services).filter(s => services[s]);
      formPayload.append('services', JSON.stringify(activeServices));

      const res = await axios.post(`${config.apiUrl || config}/leads/${id}/convert`, formPayload, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data' 
        }
      });
      setSuccess('Lead converted to Quote successfully!');
      const quoteId = res.data.quoteid || (res.data.data || {}).quoteid;
      if (quoteId) {
        setTimeout(() => navigate(`/quotes/${quoteId}`), 1500);
      } else {
        setTimeout(() => navigate(`/leads/${id}`), 2000);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to convert lead.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f4f6f8' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      
      {/* Header Section Matches LeadDetails */}
      <Paper elevation={0} sx={{ 
          p: { xs: 3, md: 4 }, 
          mb: 4, 
          mt: 2,
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)'
        }}>
          {/* Background Decorative Circles */}
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={() => navigate(`/leads/${id}`)}
                  sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                >
                  Back to Lead
                </Button>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, mb: 1 }}>
                  <Typography variant="h3" component="h1" fontWeight="800" sx={{ letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }, color: 'white' }}>
                    Convert Lead to Quote
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ color: '#e3f2fd', maxWidth: 600, fontSize: '1.1rem' }}>
                  Fill in the details below to convert this lead.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            
            {/* Column 1: Project Basic Details */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', mb: { xs: 3, md: 0 }, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eff6ff', p: 1.5, borderRadius: 2 }}>
                        <AssignmentIcon sx={{ color: '#3b82f6' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                      Basic Details
                    </Typography>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Client Selection</FormLabel>
                    <RadioGroup
                      row
                      name="clientOption"
                      value={clientOption}
                      onChange={(e) => setClientOption(e.target.value)}
                    >
                      <FormControlLabel value="existing" control={<Radio color="primary" />} label="Use Existing Client" />
                      <FormControlLabel value="new" control={<Radio color="primary" />} label="Create New Client" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {clientOption === 'existing' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      options={accounts}
                      getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || '')}
                      value={selectedAccount}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                          setSelectedAccount(null);
                          setAccountSearch(newValue);
                        } else {
                          setSelectedAccount(newValue);
                        }
                      }}
                      inputValue={accountSearch}
                      onInputChange={(event, newInputValue) => {
                        setAccountSearch(newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Account Name" 
                          placeholder="Select existing or type to search" 
                          fullWidth 
                          required={clientOption === 'existing'}
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>
                )}

                {clientOption === 'new' && (
                  <>
                    <Grid item xs={12}>
                      <TextField fullWidth label="New Client Name" name="newClientName" variant="outlined" value={formData.newClientName} onChange={handleInputChange} required={clientOption === 'new'} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Email" name="newClientEmail" variant="outlined" value={formData.newClientEmail} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Phone" name="newClientPhone" variant="outlined" value={formData.newClientPhone} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                          <InputLabel>Client Country</InputLabel>
                          <Select
                              name="newClientCountryId"
                              value={formData.newClientCountryId}
                              label="Client Country"
                              onChange={handleInputChange}
                          >
                              {countries.map((c) => (
                                  <MenuItem key={c.id} value={c.id}>
                                      {c.name}
                                  </MenuItem>
                              ))}
                          </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Additional Contact Info" name="newClientContactInfo" variant="outlined" multiline rows={2} value={formData.newClientContactInfo} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>Required Documents</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            License 1 (Img)
                            <input type="file" hidden name="license_1" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.license_1 && <Typography variant="caption" display="block">{files.license_1.name}</Typography>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            License 2 (Img)
                            <input type="file" hidden name="license_2" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.license_2 && <Typography variant="caption" display="block">{files.license_2.name}</Typography>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            Tax Card (Img)
                            <input type="file" hidden name="tax_card" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.tax_card && <Typography variant="caption" display="block">{files.tax_card.name}</Typography>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            Est. Card (Img)
                            <input type="file" hidden name="establishment_card" accept="image/*" onChange={handleFileChange} />
                        </Button>
                        {files.establishment_card && <Typography variant="caption" display="block">{files.establishment_card.name}</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth size="small">
                            Application Form (PDF)
                            <input type="file" hidden name="application_pdf" accept="application/pdf" onChange={handleFileChange} />
                        </Button>
                        {files.application_pdf && <Typography variant="caption" display="block">{files.application_pdf.name}</Typography>}
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}><Box sx={{ borderBottom: '1px solid #eee', my: 1 }} /></Grid>

                <Grid item xs={12}>
                  <TextField fullWidth label="Project Name" name="projectName" variant="outlined" value={formData.projectName} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={crmOptions.country || []}
                    getOptionLabel={(option) => option?.label || ''}
                    value={(crmOptions.country || []).find(c => c.value === formData.country) || null}
                    onChange={(e, val) => setFormData({...formData, country: val ? val.value : ''})}
                    renderInput={(params) => <TextField {...params} label="Country" variant="outlined" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Division</InputLabel>
                    <Select name="division" value={formData.division} onChange={handleInputChange} label="Division">
                      {crmOptions.division?.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {(() => {
                  const selectedCountry = crmOptions.country?.find(c => c.value === formData.country)?.label;
                  if (selectedCountry && selectedCountry.toLowerCase() === 'saudi arabia') {
                    return (
                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined" required>
                          <InputLabel>Area</InputLabel>
                          <Select name="areaKsa" value={formData.areaKsa} onChange={handleInputChange} label="Area">
                            <MenuItem value=""><em>--Select--</em></MenuItem>
                            {crmOptions.areaKsa?.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }
                  return null;
                })()}

              </Grid>
            </Paper>
          </Grid>
          
          {/* Column 2: Services & Sub Service Types */}
          <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', mb: { xs: 3, md: 0 }, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fdf4ff', p: 1.5, borderRadius: 2 }}>
                        <CategoryIcon sx={{ color: '#d946ef' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                      Services & Types
                    </Typography>                  </Box>              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Services</FormLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Object.keys(services).map((service) => (
                        <Paper key={service} variant="outlined" sx={{ px: 2, py: 0.5, borderRadius: 2 }}>
                          <FormControlLabel
                            sx={{ width: '100%', ml: 0 }}
                            control={<Checkbox checked={services[service]} onChange={handleServiceChange} name={service} color="warning" />}
                            label={<Typography variant="body1">{service}</Typography>}
                          />
                        </Paper>
                      ))}
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12}><Box sx={{ borderBottom: '1px solid #eee', my: 1 }} /></Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Industry</InputLabel>
                    <Select name="industry" value={formData.industry} onChange={handleInputChange} label="Industry">
                      {crmOptions.industry?.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {(() => {
                  const selectedIndustry = crmOptions.industry?.find(i => i.value === formData.industry)?.label;
                  if (selectedIndustry && selectedIndustry.toLowerCase() === 'buildings') {
                    return (
                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined" required>
                          <InputLabel>Buildings - Industry Type</InputLabel>
                          <Select name="buildingsIndustryType" value={formData.buildingsIndustryType} onChange={handleInputChange} label="Buildings - Industry Type">
                            <MenuItem value=""><em>--Select--</em></MenuItem>
                            {crmOptions.buildingsIndustryType?.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }
                  return null;
                })()}

                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Opportunity Source</InputLabel>
                    <Select name="opportunitySource" value={formData.opportunitySource} onChange={handleInputChange} label="Opportunity Source">
                      {crmOptions.opportunitySource?.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Column 3: Opportunity Status */}
          <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fef2f2', p: 1.5, borderRadius: 2 }}>
                        <AccessTimeFilledIcon sx={{ color: '#ef4444' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                      Status
                    </Typography>
                  </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Currency</InputLabel>
                    <Select name="currency" value={formData.currency} onChange={handleInputChange} label="Currency">
                      {crmOptions.currency?.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Price List</InputLabel>
                    <Select name="priceList" value={formData.priceList} onChange={handleInputChange} label="Price List">
                      <MenuItem value=""><em>--Select--</em></MenuItem>
                      {(crmOptions.priceList || [])
                        .filter(pl => !formData.currency || !pl.currency_id || pl.currency_id === formData.currency)
                        .map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Exchange Rate" name="exchangeRate" type="number" variant="outlined" inputProps={{ step: "0.01" }} value={formData.exchangeRate} onChange={handleInputChange} />
                </Grid>
              </Grid>

              {/* Push the button to the bottom if container is larger */}
              <Box sx={{ mt: 'auto', pt: 4 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading} 
                  size="large" 
                  fullWidth
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 2, 
                    fontSize: '1rem', 
                    fontWeight: 'bold', 
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    boxShadow: '0 8px 16px rgba(76, 175, 80, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Convert to Quote'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

    </Container>
  );
};

export default ConvertLead;
